import React from 'react'
import { useTranslation } from 'react-i18next'

import { ACHFormValues } from 'adminSignUp/common/ACHPayment'
import { Col, Row } from 'antd'
import { Formik } from 'formik'
import moment from 'moment'
import * as Yup from 'yup'

import Error from 'common/components/dataEntry/formik/FormItemError'
import CheckBox from 'common/components/dataEntry/formik/FormikCheckboxField'
import DefaultInput from 'common/components/dataEntry/formik/FormikDefaultInputField'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import { numberOnlyRegx } from 'common/components/utils/Regx'

import { parseZoneFormat } from 'common/dateFormatters'
import { useBreakPoints } from 'common/hooks'

const achFormValidator = (translate: any) => {
  return Yup.object({
    bankAccountType: Yup.string().required(translate('general.required')).typeError(translate('general.required')),
    agreeTerms: Yup.bool().oneOf([true], translate('general.required')),
    bankRoutingNumber: Yup.string().trim().required(translate('general.required')),
    accountNumber: Yup.string()
      .required(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    accountNumberConfirmation: Yup.string()
      .oneOf([Yup.ref('accountNumber')], translate('admin-signup.subscription.errors.account-number-confirmation'))
      .required(translate('general.required'))
      .typeError(translate('general.required'))
      .matches(numberOnlyRegx, translate('admin-signup.subscription.errors.account-number-regex')),
    nameOnAccount: Yup.string().trim().required(translate('general.required')),
  })
}

export interface RenewalAchFormProps {
  achFormValues: ACHFormValues
  recurlyErrors: Map<string, string>
  submitACH: (value: ACHFormValues) => void
}

const RenewalAchForm: React.FC<RenewalAchFormProps> = ({ achFormValues, recurlyErrors, submitACH }) => {
  const { isLg } = useBreakPoints()
  const { t: translate } = useTranslation()
  return (
    <Formik
      validationSchema={achFormValidator(translate)}
      initialValues={achFormValues}
      onSubmit={(values) => submitACH(values)}
    >
      {(formik) => (
        <form autoComplete="off" id="paymentInformationForm" onSubmit={formik.handleSubmit}>
          <Row className="mb-16">
            <Col md={14}>
              <Radio.Group name="bankAccountType" defaultValue={achFormValues.bankAccountType}>
                <Row>
                  <Col xs={12}>
                    <Radio.Button value="checking">
                      {translate('admin-signup.subscription.payment.checking-label')}
                    </Radio.Button>
                  </Col>
                  <Col xs={12}>
                    <Radio.Button value="savings">
                      {translate('admin-signup.subscription.payment.saving-label')}
                    </Radio.Button>
                  </Col>
                </Row>
              </Radio.Group>
            </Col>
          </Row>
          {recurlyErrors.get('account_type') ? <Error>{recurlyErrors.get('account_type')}</Error> : null}

          <Row>
            <Col span={24}>
              <DefaultInput
                label={translate('admin-signup.subscription.payment.routing-number-label')}
                name="bankRoutingNumber"
              />
              {recurlyErrors.get('routing_number') ? <Error>{recurlyErrors.get('routing_number')}</Error> : null}
            </Col>
          </Row>

          <Row>
            <Col md={24}>
              <div className={isLg ? 'flex_space_between' : ''}>
                <Col md={11}>
                  <DefaultInput
                    label={translate('admin-signup.subscription.payment.account-number-label')}
                    name="accountNumber"
                  />
                  {recurlyErrors.get('account_number') ? <Error>{recurlyErrors.get('account_number')}</Error> : null}
                </Col>
                <Col md={11}>
                  <DefaultInput
                    label={translate('admin-signup.subscription.payment.confirm-account-number-label')}
                    name="accountNumberConfirmation"
                  />
                  {recurlyErrors.get('account_number_confirmation') ? (
                    <Error>{recurlyErrors.get('account_number_confirmation')}</Error>
                  ) : null}
                </Col>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={24}>
              <div className="flex_space_between">
                <Col md={24}>
                  <DefaultInput
                    label={translate('admin-signup.subscription.payment.account-name-label')}
                    name="nameOnAccount"
                  />
                  {recurlyErrors.get('name_on_account') ? <Error>{recurlyErrors.get('name_on_account')}</Error> : null}
                </Col>
              </div>
            </Col>
          </Row>
          <Row className="form__row">
            <CheckBox name="agreeTerms" className="no-select">
              {translate('admin-signup.subscription.payment.accept-label')}
            </CheckBox>
          </Row>
          <Row className="form__row">
            <div className="info-card">
              <p>
                {translate('admin-signup.subscription.payment.agreement-1').replace(
                  '[CurrentDate]',
                  parseZoneFormat(moment()),
                )}
              </p>
              <p>{translate('admin-signup.subscription.payment.agreement-2')}</p>
              <p>
                {translate('admin-signup.subscription.payment.agreement-3').replace(
                  '[CurrentDate]',
                  parseZoneFormat(moment()),
                )}
              </p>
            </div>
          </Row>
        </form>
      )}
    </Formik>
  )
}

export default RenewalAchForm
